import { c as _c } from "react/compiler-runtime";
import { Select } from '@onefootprint/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { type FieldType, isEnumType, isObjectType } from '../../../store/types';
import { padEnumValues } from '../../../store/utils';
import type { SchemaEditorState, SchemaPatch } from '../../../store/wire';
import { ClickToReveal, TypeChip } from '../../shared/inline-edit';
import { ChildPropertiesEditor } from './child-properties-editor';
import { EnumValuesInput } from './enum-values-input';
import { FIELD_TYPE_BADGE, useFieldTypeOptions } from './field-type-options';
import { PropertyTreePreview } from './property-tree-preview';
import { getSchemaPatchForTypeChange } from './schema-state';
const EnumSummary = t0 => {
  const $ = _c(11);
  const {
    values
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.task.field"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  let t2;
  if ($[1] !== values) {
    t2 = new Set((values ?? []).map(_temp).filter(Boolean));
    $[1] = values;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = [...t2];
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const filled = t3;
  if (filled.length === 0) {
    let t4;
    if ($[5] !== t) {
      t4 = t(_temp2);
      $[5] = t;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== t4) {
      t5 = <span className="text-snippet-3 text-tertiary">{t4}</span>;
      $[7] = t4;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  let t4;
  if ($[9] !== filled) {
    t4 = <span className="flex flex-wrap gap-1">{filled.map(_temp3)}</span>;
    $[9] = filled;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
const ObjectSummary = t0 => {
  const $ = _c(10);
  const {
    count
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.task.field"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  if (count === 0) {
    let t2;
    if ($[1] !== t) {
      t2 = t(_temp4);
      $[1] = t;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== t2) {
      t3 = <span className="text-snippet-3 text-tertiary">{t2}</span>;
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  let t2;
  if ($[5] !== count || $[6] !== t) {
    t2 = count === 1 ? t(_temp5) : t(_temp6, {
      count
    });
    $[5] = count;
    $[6] = t;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t2) {
    t3 = <span className="text-snippet-3 text-tertiary">{t2}</span>;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
type SchemaTypeSelectProps = {
  id: string;
  ariaLabel: string;
  schema: SchemaEditorState;
  canEdit: boolean;
  onChange: (patch: SchemaPatch, autoOpenEditor: boolean) => void;
};
const TypeSelect = t0 => {
  const $ = _c(18);
  const {
    id,
    ariaLabel,
    schema,
    canEdit,
    allowDocument,
    onChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.task.field"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const options = useFieldTypeOptions(allowDocument);
  let t2;
  if ($[1] !== onChange || $[2] !== schema) {
    t2 = value => {
      const {
        patch,
        autoOpenEditor
      } = getSchemaPatchForTypeChange(schema, value as FieldType);
      onChange(patch, autoOpenEditor);
    };
    $[1] = onChange;
    $[2] = schema;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleTypeChange = t2;
  let t3;
  if ($[4] !== schema.type || $[5] !== t) {
    t3 = schema.type === null ? t(_temp7) : FIELD_TYPE_BADGE[schema.type];
    $[4] = schema.type;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const triggerLabel = t3;
  const t4 = schema.type ?? "";
  const t5 = !canEdit;
  let t6;
  if ($[7] !== canEdit || $[8] !== triggerLabel) {
    t6 = <TypeChip label={triggerLabel} canEdit={canEdit} />;
    $[7] = canEdit;
    $[8] = triggerLabel;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== ariaLabel || $[11] !== handleTypeChange || $[12] !== id || $[13] !== options || $[14] !== t4 || $[15] !== t5 || $[16] !== t6) {
    t7 = <Select id={id} aria-label={ariaLabel} value={t4} options={options} disabled={t5} onChange={handleTypeChange} customTrigger={t6} />;
    $[10] = ariaLabel;
    $[11] = handleTypeChange;
    $[12] = id;
    $[13] = options;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
};
export const SchemaTypeSelect = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <TypeSelect {...props} allowDocument={false} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};

// Only a top-level agent input can be a document: the backend resolves the pointer there and nowhere else.
export const InputTypeSelect = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <TypeSelect {...props} allowDocument={true} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
type SchemaDetailsEditorProps = {
  schema: SchemaEditorState;
  canEdit: boolean;
  autoOpen?: boolean;
  onChange: (patch: SchemaPatch) => void;
  children?: ReactNode;
};
export const SchemaDetailsEditor = t0 => {
  const $ = _c(19);
  const {
    schema,
    canEdit,
    autoOpen,
    onChange,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.task.field"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  let t2;
  if ($[1] !== autoOpen || $[2] !== canEdit || $[3] !== onChange || $[4] !== schema.enumValues || $[5] !== schema.type || $[6] !== t) {
    t2 = isEnumType(schema.type) && <ClickToReveal canEdit={canEdit} autoOpen={autoOpen} ariaLabel={t(_temp8)} className="w-full px-1 py-0.5" display={<EnumSummary values={schema.enumValues} />}>{() => <EnumValuesInput values={padEnumValues(schema.enumValues)} onChange={values => onChange({
        enumValues: values
      })} />}</ClickToReveal>;
    $[1] = autoOpen;
    $[2] = canEdit;
    $[3] = onChange;
    $[4] = schema.enumValues;
    $[5] = schema.type;
    $[6] = t;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== autoOpen || $[9] !== canEdit || $[10] !== onChange || $[11] !== schema.children || $[12] !== schema.type || $[13] !== t) {
    t3 = isObjectType(schema.type) && (canEdit ? <ClickToReveal canEdit={canEdit} autoOpen={autoOpen} ariaLabel={t(_temp9)} className="w-full px-1 py-0.5" display={<ObjectSummary count={schema.children?.length ?? 0} />}>{() => <ChildPropertiesEditor properties={schema.children ?? []} onChange={properties => onChange({
        children: properties
      })} />}</ClickToReveal> : <div className="w-full px-1 py-0.5"><PropertyTreePreview properties={schema.children ?? []} /></div>);
    $[8] = autoOpen;
    $[9] = canEdit;
    $[10] = onChange;
    $[11] = schema.children;
    $[12] = schema.type;
    $[13] = t;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== children || $[16] !== t2 || $[17] !== t3) {
    t4 = <>{t2}{t3}{children}</>;
    $[15] = children;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
};
function _temp(v) {
  return v.trim();
}
function _temp2($) {
  return $.enum.empty;
}
function _temp3(value) {
  return <span key={value} className="rounded bg-secondary px-1.5 py-0.5 text-snippet-3 text-secondary">{value}</span>;
}
function _temp4($) {
  return $.object.empty;
}
function _temp5($_0) {
  return $_0.object.oneProperty;
}
function _temp6($_1) {
  return $_1.object.manyProperties;
}
function _temp7($) {
  return $.typePlaceholder;
}
function _temp8($) {
  return $.enum.label;
}
function _temp9($_0) {
  return $_0.object.label;
}